'use client';

import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  ATTRIBUTION_COOKIE,
  ATTRIBUTION_MAX_AGE_SECONDS,
  CONSENT_COOKIE,
  CONSENT_MAX_AGE_SECONDS,
  captureAttributionIfConsented,
  claimCanonicalEvent,
  consentFromAction,
  consentModeV2,
  firstToolEventId,
  dataLayerHasConfig,
  googleLinkerSettings,
  googleScriptSrc,
  isAuthCallbackLocation,
  pageViewEventId,
  parseConsent,
  planGoogleTagInstall,
  planTagLoads,
  platformTagsEnabled,
  readCookie,
  recordConsentedVisit,
  recordViewTool,
  rehubAttribution,
  rehubBrowserTarget,
  serializeCookie,
  sharedCookieDomain,
  shouldAppendGoogleScript,
  shouldFireClaimedConversion,
  signupEventId,
  tagConfigFromEnv,
  trackingCutoverEnabled,
  viewToolEventId,
  type ConsentAction,
  type StoredConsent,
  type TrackingRpc,
} from './index';
import { dispatchBrowserEvent } from './browser';

export interface RehubTrackingProps {
  app: string;
  /**
   * `full` shows the consent dialog and loads tags after consent.
   * `events-only` records consented first-party events and does not add tags.
   * Pages that still have a legacy snippet must stay on `events-only` until cutover.
   */
  mode?: 'full' | 'events-only';
  userId?: string | null;
  rpc?: TrackingRpc;
  env?: Record<string, string | undefined>;
  /**
   * Public tour websites pass false so visitors are not asked to accept cookies.
   * The studio app keeps the dialog.
   */
  showConsentBanner?: boolean;
}

function readRuntimeEnv(explicit?: Record<string, string | undefined>): Record<string, string | undefined> {
  if (explicit) return explicit;
  const vite = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
  const next = typeof process !== 'undefined' && process.env ? (process.env as Record<string, string | undefined>) : {};
  // Next inlines NEXT_PUBLIC_* only when the property is written out in source.
  // Reading process.env as a whole object leaves the cutover flag undefined.
  const named = typeof process !== 'undefined' && process.env
    ? {
        NEXT_PUBLIC_REHUB_TRACKING_CUTOVER: process.env.NEXT_PUBLIC_REHUB_TRACKING_CUTOVER,
        NEXT_PUBLIC_REHUB_TRACKING_DEBUG: process.env.NEXT_PUBLIC_REHUB_TRACKING_DEBUG,
        NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
      }
    : {};
  return { ...next, ...vite, ...named };
}

function writeBrowserCookie(name: string, value: string, maxAgeSeconds: number): void {
  const domain = sharedCookieDomain(window.location.hostname);
  document.cookie = serializeCookie(name, value, {
    domain,
    maxAgeSeconds,
    secure: window.location.protocol === 'https:',
  });
}

function currentPath(): string {
  const hash = window.location.hash.replace(/^#/, '');
  const hashPath = hash.split('?')[0];
  if (hashPath.startsWith('/')) return hashPath.slice(0, 500);
  return window.location.pathname.slice(0, 500) || '/';
}

function sessionId(): string {
  const key = 'rehub_session';
  try {
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const created = window.crypto?.randomUUID?.() || `s${Date.now()}`;
    window.sessionStorage.setItem(key, created);
    return created;
  } catch {
    return 'session';
  }
}

function installedTags(): { ga4?: string; googleAds?: string; meta?: string; linkedin?: string } {
  const w = window as Window & { __rehubTracking?: { ga4?: string; googleAds?: string; meta?: string; linkedin?: string } };
  w.__rehubTracking = w.__rehubTracking || {};
  return w.__rehubTracking;
}

function foreignGtagPresent(config: { ga4MeasurementId?: string; googleAdsId?: string }): boolean {
  const w = window as Window & { gtag?: unknown; __rehubTracking?: { ga4?: string; googleAds?: string } };
  const mark = w.__rehubTracking;
  const ownsGtag = mark?.ga4 === config.ga4MeasurementId || mark?.googleAds === config.googleAdsId;
  return typeof w.gtag === 'function' && !ownsGtag;
}

function foreignMetaPresent(config: { metaPixelId?: string }): boolean {
  const w = window as Window & { fbq?: unknown; __rehubTracking?: { meta?: string } };
  return typeof w.fbq === 'function' && w.__rehubTracking?.meta !== config.metaPixelId;
}

/** One gtag.js for GA4 and Google Ads. A second script is never added. */
function installGoogleTags(input: {
  ga4MeasurementId?: string;
  googleAdsId?: string;
  consent: StoredConsent;
  appName: string;
  debugMode: boolean;
  reuseExistingGtag: boolean;
  loadScript: boolean;
}): void {
  const w = window as Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== 'function') {
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
  }
  const mark = installedTags();
  const domain = sharedCookieDomain(window.location.hostname);
  const cookieDomain = domain ? { cookie_domain: domain } : {};
  const linker = googleLinkerSettings();
  if (!input.reuseExistingGtag) {
    w.gtag('consent', 'default', consentModeV2(null));
    w.gtag('consent', 'update', consentModeV2(input.consent));
    w.gtag('set', 'ads_data_redaction', true);
    w.gtag('set', 'url_passthrough', true);
    w.gtag('set', 'linker', linker);
    w.gtag('js', new Date());
  } else if (input.consent.advertising) {
    w.gtag('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
    });
    w.gtag('set', 'linker', linker);
  }
  if (input.ga4MeasurementId && mark.ga4 !== input.ga4MeasurementId && !dataLayerHasConfig(w.dataLayer, input.ga4MeasurementId)) {
    w.gtag('set', 'user_properties', { app_name: input.appName });
    w.gtag('config', input.ga4MeasurementId, {
      send_page_view: false,
      anonymize_ip: true,
      linker,
      ...cookieDomain,
      ...(input.debugMode ? { debug_mode: true } : {}),
    });
    mark.ga4 = input.ga4MeasurementId;
  }
  if (
    input.googleAdsId
    && input.consent.advertising
    && mark.googleAds !== input.googleAdsId
    && !dataLayerHasConfig(w.dataLayer, input.googleAdsId)
  ) {
    w.gtag('config', input.googleAdsId, { ...cookieDomain, linker, send_page_view: false });
    mark.googleAds = input.googleAdsId;
  } else if (input.googleAdsId && (mark.googleAds === input.googleAdsId || dataLayerHasConfig(w.dataLayer, input.googleAdsId))) {
    mark.googleAds = input.googleAdsId;
  }
  if (!input.loadScript || document.getElementById('rehub-ga4')) return;
  const scriptId = input.ga4MeasurementId || input.googleAdsId;
  if (!scriptId) return;
  const script = document.createElement('script');
  script.id = 'rehub-ga4';
  script.async = true;
  script.src = googleScriptSrc(scriptId);
  document.head.appendChild(script);
}

function installMeta(pixelId: string): void {
  const w = window as Window & { fbq?: MetaFbq; _fbq?: unknown };
  if (typeof w.fbq === 'function') return;
  const fbq = function fbq(...args: unknown[]) {
    const fn = fbq as MetaFbq;
    if (fn.callMethod) fn.callMethod(...args);
    else fn.queue.push(args);
  } as MetaFbq;
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = '2.0';
  w.fbq = fbq;
  w._fbq = fbq;
  const script = document.createElement('script');
  script.id = 'rehub-meta';
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  fbq('init', pixelId);
  installedTags().meta = pixelId;
}

interface MetaFbq {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  loaded: boolean;
  version: string;
}

interface LinkedInTracker {
  (...args: unknown[]): void;
  q: unknown[];
}

function installLinkedIn(partnerId: string): void {
  const w = window as Window & { _linkedin_partner_id?: string; _linkedin_data_partner_ids?: string[]; lintrk?: (...args: unknown[]) => void };
  if (w._linkedin_data_partner_ids?.includes(partnerId)) return;
  w._linkedin_partner_id = partnerId;
  w._linkedin_data_partner_ids = w._linkedin_data_partner_ids || [];
  w._linkedin_data_partner_ids.push(partnerId);
  if (typeof w.lintrk !== 'function') {
    const lintrk = function lintrk(...args: unknown[]) {
      (lintrk as LinkedInTracker).q.push(args);
    } as LinkedInTracker;
    lintrk.q = [];
    w.lintrk = lintrk;
  }
  if (!document.getElementById('rehub-linkedin')) {
    const script = document.createElement('script');
    script.id = 'rehub-linkedin';
    script.async = true;
    script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    document.head.appendChild(script);
  }
}

function loadPlannedTags(consent: StoredConsent, env: Record<string, string | undefined>, appName: string): void {
  try {
    const config = tagConfigFromEnv(env);
    const foreignGtag = foreignGtagPresent(config);
    const w = window as Window & { gtag?: (...args: unknown[]) => void; fbq?: unknown; _linkedin_data_partner_ids?: string[] };
    const mark = installedTags();
    const already = {
      ga4: mark.ga4 === config.ga4MeasurementId && config.ga4MeasurementId ? [config.ga4MeasurementId] : [],
      googleAds: mark.googleAds === config.googleAdsId && config.googleAdsId ? [config.googleAdsId] : [],
      meta: mark.meta === config.metaPixelId && config.metaPixelId ? [config.metaPixelId] : [],
      linkedin: config.linkedInPartnerId && w._linkedin_data_partner_ids?.includes(config.linkedInPartnerId) ? [config.linkedInPartnerId] : [],
    };
    const google = planGoogleTagInstall({ config, consent, already, foreignGtag });
    if (google.configureGa4 || google.configureAds) {
      installGoogleTags({
        ga4MeasurementId: google.configureGa4,
        googleAdsId: google.configureAds,
        consent,
        appName,
        debugMode: Boolean(config.debugMode),
        reuseExistingGtag: google.reuseExistingGtag,
        loadScript: shouldAppendGoogleScript({
          plan: google,
          scriptAlreadyPresent: Boolean(document.getElementById('rehub-ga4')),
        }),
      });
    }
    const plan = planTagLoads(config, consent, already);
    if (plan.metaPixelId && !foreignMetaPresent(config)) installMeta(plan.metaPixelId);
    if (plan.linkedInPartnerId) {
      installLinkedIn(plan.linkedInPartnerId);
      mark.linkedin = plan.linkedInPartnerId;
    }
    if ((mark.ga4 || mark.googleAds) && typeof w.gtag === 'function' && !foreignGtag) {
      w.gtag('consent', 'update', consentModeV2(consent));
    }
  } catch {
    /* tracking must not break the app */
  }
}

function subscribeLocation(onChange: () => void): () => void {
  window.addEventListener('hashchange', onChange);
  window.addEventListener('popstate', onChange);
  const { pushState, replaceState } = history;
  history.pushState = function pushStateAndNotify(...args) {
    pushState.apply(this, args);
    onChange();
  };
  history.replaceState = function replaceStateAndNotify(...args) {
    replaceState.apply(this, args);
    onChange();
  };
  return () => {
    window.removeEventListener('hashchange', onChange);
    window.removeEventListener('popstate', onChange);
    history.pushState = pushState;
    history.replaceState = replaceState;
  };
}

function memoryStore() {
  return window.localStorage;
}

export function RehubTracking({ app, mode = 'full', userId, rpc, env, showConsentBanner = true }: RehubTrackingProps) {
  const runtimeEnv = useMemo(() => env ?? readRuntimeEnv(), [env]);
  const cutover = trackingCutoverEnabled(runtimeEnv);
  const tagsOn = platformTagsEnabled({
    hostname: typeof window === 'undefined' ? '' : window.location.hostname,
    env: runtimeEnv,
  });
  const activeMode = tagsOn && (mode === 'full' || cutover) ? 'full' : 'events-only';
  const titleId = useId();
  const [consent, setConsent] = useState<StoredConsent | null>(null);
  const [ready, setReady] = useState(false);
  const [managing, setManaging] = useState(false);
  const [draftAnalytics, setDraftAnalytics] = useState(false);
  const [draftAdvertising, setDraftAdvertising] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    const stored = parseConsent(readCookie(document.cookie, CONSENT_COOKIE));
    setConsent(stored);
    setReady(true);
    if (stored && tagsOn && (mode === 'full' || cutover)) loadPlannedTags(stored, runtimeEnv, app);
  }, [tagsOn, env, mode, cutover, app, runtimeEnv]);

  useEffect(() => {
    if (!ready || !consent || (!consent.analytics && !consent.advertising)) return;
    captureAttributionIfConsented({
      pathname: window.location.pathname,
      search: window.location.search || (window.location.hash.includes('?') ? window.location.hash.slice(window.location.hash.indexOf('?')) : ''),
      storage: memoryStore(),
      consent,
    });
    const touch = rehubAttribution.readFirstTouch(memoryStore());
    if (touch) {
      writeBrowserCookie(ATTRIBUTION_COOKIE, JSON.stringify(rehubAttribution.attributionPayload(touch)), ATTRIBUTION_MAX_AGE_SECONDS);
    }
  }, [ready, consent]);

  useEffect(() => {
    if (!ready || activeMode !== 'full' || !consent || (!consent.analytics && !consent.advertising)) return;
    const send = () => {
      try {
        const config = tagConfigFromEnv(runtimeEnv);
        const mark = (window as Window & { __rehubTracking?: { ga4?: string } }).__rehubTracking;
        if (mark?.ga4 !== config.ga4MeasurementId) return;
        const target = rehubBrowserTarget(window, config);
        if (!target.gtag && !target.fbq) return;
        dispatchBrowserEvent({
          event: 'page_view',
          eventId: pageViewEventId(sessionId(), currentPath()),
          params: { app_name: app, app, content_name: app, page_path: currentPath() },
          config,
          consent,
          storage: window.sessionStorage,
          target,
        });
      } catch {
        /* tracking must not break the app */
      }
    };
    send();
    window.addEventListener('hashchange', send);
    window.addEventListener('popstate', send);
    return () => {
      window.removeEventListener('hashchange', send);
      window.removeEventListener('popstate', send);
    };
  }, [ready, activeMode, consent, app, env, runtimeEnv]);

  useEffect(() => {
    if (!ready || !rpc || !consent?.analytics) return;
    const send = () => {
      const touch = rehubAttribution.readFirstTouch(memoryStore());
      void recordConsentedVisit(rpc, {
        sessionId: sessionId(),
        path: currentPath(),
        app,
        acquisition: rehubAttribution.attributionPayload(touch),
      });
    };
    send();
    window.addEventListener('hashchange', send);
    window.addEventListener('popstate', send);
    return () => {
      window.removeEventListener('hashchange', send);
      window.removeEventListener('popstate', send);
    };
  }, [ready, rpc, consent, app]);

  useEffect(() => {
    if (!ready || !rpc || !userId || !consent) return;
    if (consent.analytics) {
      void recordViewTool(rpc, { sessionId: sessionId(), app });
    }
    // Leave the row pending until the shared loader can send it. Claiming in
    // events-only mode would mark it sent and the later cutover could not fire.
    if (activeMode === 'full' && (consent.analytics || consent.advertising)) {
      const config = tagConfigFromEnv(runtimeEnv);
      const mark = (window as Window & { __rehubTracking?: { ga4?: string } }).__rehubTracking;
      const target = rehubBrowserTarget(window, config);
      if (mark?.ga4 === config.ga4MeasurementId && (target.gtag || target.fbq)) {
        dispatchBrowserEvent({
          event: 'view_tool',
          eventId: viewToolEventId(app, sessionId()),
          params: { app_name: app, app, content_name: app },
          config,
          consent,
          storage: window.sessionStorage,
          target,
        });
      }
    }
    if (activeMode !== 'full' || (!consent.analytics && !consent.advertising)) return;
    let signupId = '';
    let toolId = '';
    try {
      signupId = signupEventId(userId);
      toolId = firstToolEventId(userId);
    } catch {
      return;
    }
    let cancelled = false;
    let started = false;
    const claimSettledEvents = () => {
      if (cancelled || started || isAuthCallbackLocation(window.location.href)) return;
      started = true;
      const touch = rehubAttribution.readFirstTouch(memoryStore());
      const acquisition = rehubAttribution.attributionPayload(touch);
      const fire = (event: 'sign_up' | 'first_tool_use', row: Record<string, unknown> | null) => {
        try {
          if (cancelled || !shouldFireClaimedConversion(row) || !row || typeof row.event_id !== 'string') return;
          const config = tagConfigFromEnv(runtimeEnv);
          const target = rehubBrowserTarget(window, config);
          if (!target.gtag && !target.fbq && !target.lintrk) return;
          dispatchBrowserEvent({
            event,
            eventId: row.event_id,
            params: event === 'sign_up'
              ? { method: 'rehub', app_name: app, app, content_name: app }
              : { app_name: app, app, content_name: app },
            config,
            consent,
            storage: window.sessionStorage,
            target,
          });
        } catch {
          /* tracking must not break the app */
        }
      };
      void claimCanonicalEvent(rpc, signupId, consent, acquisition).then((row) => fire('sign_up', row));
      void claimCanonicalEvent(rpc, toolId, consent, acquisition).then((row) => fire('first_tool_use', row));
    };
    claimSettledEvents();
    const stopLocation = subscribeLocation(claimSettledEvents);
    return () => {
      cancelled = true;
      stopLocation();
    };
  }, [ready, rpc, userId, consent, app, activeMode, env, runtimeEnv]);

  function apply(action: ConsentAction) {
    const next = consentFromAction(
      action,
      new Date().toISOString(),
      action === 'save' ? { analytics: draftAnalytics, advertising: draftAdvertising } : undefined,
    );
    writeBrowserCookie(CONSENT_COOKIE, JSON.stringify(next), CONSENT_MAX_AGE_SECONDS);
    setConsent(next);
    setManaging(false);
    setPrefsOpen(false);
    if (activeMode === 'full') loadPlannedTags(next, runtimeEnv, app);
  }

  if (!ready || activeMode !== 'full' || !showConsentBanner) return null;

  if (!consent || prefsOpen) {
    return (
      <div
        className="fixed inset-x-0 bottom-0 z-[80] p-4 print:hidden"
        data-rehub-consent=""
        style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 80, padding: '1rem' }}
      >
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          className="mx-auto max-w-3xl rounded-2xl border border-slate-700 bg-slate-950 p-5 text-slate-100"
        >
          <h2 id={titleId} className="text-base font-semibold">Cookies and advertising</h2>
          <p className="mt-2 text-sm text-slate-300">
            Essential cookies keep you signed in. Analytics and advertising cookies stay off unless you allow them.
            You can change this choice at any time.
          </p>
          {managing && (
            <fieldset className="mt-4 space-y-2 text-sm">
              <legend className="sr-only">Optional cookies</legend>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked disabled readOnly />
                Essential (always on)
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={draftAnalytics} onChange={(event) => setDraftAnalytics(event.target.checked)} />
                Analytics
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={draftAdvertising} onChange={(event) => setDraftAdvertising(event.target.checked)} />
                Advertising
              </label>
            </fieldset>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="rounded-full bg-teal-600 px-4 py-2 text-sm font-semibold text-white" onClick={() => apply('accept_all')}>
              Accept all
            </button>
            <button type="button" className="rounded-full border border-slate-600 px-4 py-2 text-sm" onClick={() => apply('reject_optional')}>
              Reject optional
            </button>
            {managing ? (
              <button type="button" className="rounded-full border border-slate-600 px-4 py-2 text-sm" onClick={() => apply('save')}>
                Save preferences
              </button>
            ) : (
              <button
                type="button"
                className="rounded-full border border-slate-600 px-4 py-2 text-sm"
                onClick={() => {
                  setDraftAnalytics(consent?.analytics === true);
                  setDraftAdvertising(consent?.advertising === true);
                  setManaging(true);
                }}
              >
                Manage preferences
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-label="Cookie preferences"
      title="Cookie preferences"
      data-rehub-consent=""
      className="print:hidden fixed bottom-4 left-4 z-[70] flex h-10 w-10 items-center justify-center rounded-full border border-slate-600 bg-slate-950 text-slate-200 shadow-lg hover:bg-slate-800"
      style={{
        position: 'fixed',
        bottom: '1rem',
        left: '1rem',
        zIndex: 70,
        display: 'flex',
        height: '2.5rem',
        width: '2.5rem',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '9999px',
        border: '1px solid rgb(71 85 105)',
        background: 'rgb(2 6 23)',
        color: 'rgb(226 232 240)',
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.35)',
        cursor: 'pointer',
        padding: 0,
      }}
      onClick={() => {
        setDraftAnalytics(consent.analytics);
        setDraftAdvertising(consent.advertising);
        setManaging(true);
        setPrefsOpen(true);
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M12 2a10 10 0 1 0 9.95 9.05 2.6 2.6 0 0 1-3.55-3.45A2.6 2.6 0 0 1 14.95 2.05 10 10 0 0 0 12 2Z" />
        <circle cx="8.2" cy="9.2" r="1.15" fill="rgb(2 6 23)" />
        <circle cx="12" cy="13.2" r="1.15" fill="rgb(2 6 23)" />
        <circle cx="15.4" cy="10.4" r="1.05" fill="rgb(2 6 23)" />
        <circle cx="9.3" cy="15.6" r="0.9" fill="rgb(2 6 23)" />
      </svg>
    </button>
  );
}

type TrackingAuthClient = {
  rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
  auth: {
    getSession: () => Promise<{ data: { session: { user: { id: string } } | null } }>;
    onAuthStateChange: (
      callback: (event: string, session: { user: { id: string } } | null) => void,
    ) => { data: { subscription: { unsubscribe: () => void } } };
  };
};

/**
 * Mounts the shared loader against an app's Supabase client.
 * Apps without a legacy snippet use this in full mode. Tags stay off until IDs are set.
 */
export function RehubSupabaseTracking({
  app,
  mode = 'full',
  supabase,
  showConsentBanner = true,
}: {
  app: string;
  mode?: 'full' | 'events-only';
  supabase: object;
  showConsentBanner?: boolean;
}) {
  const client = supabase as TrackingAuthClient;
  const [userId, setUserId] = useState<string | null>(null);
  const rpc = useCallback<TrackingRpc>(async (fn, args) => {
    const { data, error } = await client.rpc(fn, args);
    return { data, error: error ? { message: error.message } : null };
  }, [client]);

  useEffect(() => {
    let active = true;
    let unsubscribe = () => {};
    try {
      void client.auth.getSession().then(({ data }) => {
        if (active) setUserId(data.session?.user?.id ?? null);
      }).catch(() => {
        if (active) setUserId(null);
      });
      const { data } = client.auth.onAuthStateChange((_event, session) => {
        setUserId(session?.user?.id ?? null);
      });
      unsubscribe = () => data.subscription.unsubscribe();
    } catch {
      /* tracking must not break the app */
    }
    return () => {
      active = false;
      unsubscribe();
    };
  }, [client]);

  return <RehubTracking app={app} mode={mode} userId={userId} rpc={rpc} showConsentBanner={showConsentBanner} />;
}

export default RehubTracking;
